/**

  ___                       _      ___       _        
 |   \ _  _ _ _  __ _ _ __ (_)__  |   \ __ _| |_ __ _ 
 | |) | || | ' \/ _` | '  \| / _| | |) / _` |  _/ _` |
 |___/ \_, |_||_\__,_|_|_|_|_\__| |___/\__,_|\__\__,_|
       |__/
       
*/
import { AssetDTO, AssetInputDTO, AssetService, AssetTypeDTO, IdName, AssetConfigurationDTO, AssetAnalogInputDTO } from '../service/asset';
import { VisionWindowManager } from './manager';
import { VisionWindow } from './window';
import { SVGIcons } from '../map/svg';
import { Measurement } from '../components/measurement';
import { VisionApplication } from '../application';
import { PublishTopic } from '../events';

//====================================================================================================================================================================================================

//-------------------------------------------------------------------------------------------------

class EditAssetAnalogInputs {
    private owner: VisionAssetWindow;
    private layout: dhtmlXLayoutObject;
    private inputGrid: dhtmlXGridObject;

    constructor(owner: VisionAssetWindow) {
        this.owner = owner;
        const config: IdhtmlXLayoutConfig = {
            parent: document.body,
            pattern: '1C',
        };
        this.layout = this.owner.sideBar.cells('asset_analog_inputs').attachLayout(config);
        this.layout.cells('a').hideHeader();
        this.inputGrid = this.layout.cells('a').attachGrid();
        this.inputGrid.setImagesPath('css/imgs/');
        this.inputGrid.setHeader('Number,Enabled,Low Value,High Value,Activity,Indicator,Color', '', [
            'text-align:left;',
            'text-align:center;',
            'text-align:center',
            'text-align:center',
            'text-align:center',
            'text-align:center',
            'text-align:center',
        ]);
        this.inputGrid.setColTypes('ro,ch,ed,ed,coro,coro,cp');
        this.inputGrid.setColAlign('left,center,center,center,center,center');
        this.inputGrid.setInitWidths('75,75,*,*,*,*,*');
        const inputCombo = this.inputGrid.getCombo(4);
        inputCombo.put('none', 'none');
        inputCombo.put('SPREADER', 'Spreader');

        const combobox = this.inputGrid.getCombo(5);
        combobox.put('TOP', 'TOP');
        combobox.put('BOTTOM', 'BOTTOM');
        combobox.put('FRONT', 'FRONT');
        combobox.put('BACK', 'BACK');
        combobox.put('NONE', 'NONE');
        this.inputGrid.setHighlight(false);
        this.inputGrid.init();
        this.defaultGrid();
    }

    /** Create the grid with default values for the inputs.
     *
     */
    defaultGrid(): void {
        this.inputGrid.clearAll(false);

        for (let i = 0; i < 10; i++) {
            const inputInfo = [];
            inputInfo.push(i + 1);
            inputInfo.push(0);
            inputInfo.push(0);
            inputInfo.push(0);
            inputInfo.push('');
            inputInfo.push('');
            inputInfo.push('');
            this.inputGrid.addRow(i + 1, inputInfo.join(','), 0, '', '', false);
        }
        this.inputGrid.sortRows(0, 'int', 'asc');
    }

    getConfig(): AssetAnalogInputDTO[] {
        const inputs: AssetAnalogInputDTO[] = [];

        for (let i = 1; i < 10 + 1; i++) {
            const input = {} as AssetAnalogInputDTO;
            input.number = i;
            input.enabled = this.inputGrid.cells(i, 1).isChecked();
            input.low = this.inputGrid.cells(i, 2).getValue();
            input.high = this.inputGrid.cells(i, 3).getValue();
            input.activity = this.inputGrid.cells(i, 4).getValue();
            input.indicator = this.inputGrid.cells(i, 5).getValue();
            input.colour = this.inputGrid.cells(i, 6).getValue();
            inputs.push(input);
        }
        return inputs;
    }

    /** Update the grid with the info from the server.
     *
     * @param asset The AssetDTO where we can read the analog input from.
     */
    public update(asset: AssetDTO): void {
        this.defaultGrid();
        if (asset.config.analog == null) {
            return;
        }

        asset.config.analog.forEach((input) => {
            console.log('Input number: ' + input.number);
            this.inputGrid.cells(input.number, 1).setValue(input.enabled ? '1' : '0');
            this.inputGrid.cells(input.number, 2).setValue(input.low);
            this.inputGrid.cells(input.number, 3).setValue(input.high);
            this.inputGrid.cells(input.number, 4).setValue(input.activity ? input.activity : '');
            this.inputGrid.cells(input.number, 5).setValue(input.indicator ? input.indicator : '');
            this.inputGrid.cells(input.number, 6).setValue(input.colour ? input.colour : '');
        });
    }
}

/** Handles the editing of the meta data tab.
 *
 */
class EditAssetInputData {
    private owner: VisionAssetWindow;
    private layout: dhtmlXLayoutObject;
    private inputGrid: dhtmlXGridObject;

    constructor(owner: VisionAssetWindow) {
        this.owner = owner;
        const config: IdhtmlXLayoutConfig = {
            parent: document.body,
            pattern: '1C',
        };
        this.layout = this.owner.sideBar.cells('asset_digital_inputs').attachLayout(config);
        this.layout.cells('a').hideHeader();
        this.inputGrid = this.layout.cells('a').attachGrid();
        this.inputGrid.setImagesPath('css/imgs/');
        this.inputGrid.setHeader('Number,Enabled,Invert,Activity,Indicator,Color', '', [
            'text-align:left;',
            'text-align:center;',
            'text-align:center',
            'text-align:center',
            'text-align:center',
            'text-align:center',
        ]);
        this.inputGrid.setColTypes('ro,ch,ch,coro,coro,cp');
        this.inputGrid.setColAlign('left,center,center,center,center');
        this.inputGrid.setInitWidths('75,75,75,*,*,*');
        const inputCombo = this.inputGrid.getCombo(3);
        inputCombo.put('IGN', 'Ignition');
        inputCombo.put('PLOW', 'Plow');
        inputCombo.put('SPREADER', 'Spreader');
        inputCombo.put('LEFT BROOM', 'Left Broom');
        inputCombo.put('RIGHT BROOM', 'Right Broom');
        inputCombo.put('MAIN BROOM', 'Main Broom');
        inputCombo.put('BOX', 'Box Up');
        inputCombo.put('TAILGATE', 'Tailgate');
        inputCombo.put('MOLDBOARD', 'Moldboard');
        inputCombo.put('MOWER', 'Mower');
        inputCombo.put('BELLY_DUMP', 'Belly Dump');

        const combobox = this.inputGrid.getCombo(4);
        combobox.put('TOP', 'TOP');
        combobox.put('BOTTOM', 'BOTTOM');
        combobox.put('FRONT', 'FRONT');
        combobox.put('BACK', 'BACK');
        combobox.put('NONE', 'NONE');
        this.inputGrid.setHighlight(false);
        this.inputGrid.init();
        this.defaultGrid();
    }

    /** Create the grid with default values for the inputs.
     *
     */
    defaultGrid(): void {
        this.inputGrid.clearAll(false);

        for (let i = 0; i < 4; i++) {
            const inputInfo = [];
            inputInfo.push(i + 1);
            inputInfo.push(0);
            inputInfo.push(0);
            inputInfo.push('');
            inputInfo.push('');
            inputInfo.push('');
            this.inputGrid.addRow(i + 1, inputInfo.join(','), 0, '', '', false);
        }
        this.inputGrid.sortRows(0, 'int', 'asc');
    }

    /** Updates the inputs grid with info from the asset configuration.
     *
     * @param asset Asset from the server.
     */
    update(asset: AssetDTO): void {
        this.defaultGrid();
        asset.config.inputs.forEach((input) => {
            this.inputGrid.cells(input.number, 1).setValue(input.enabled ? '1' : '0');
            this.inputGrid.cells(input.number, 2).setValue(input.invert ? '1' : '0');
            this.inputGrid.cells(input.number, 3).setValue(input.activity);
            this.inputGrid.cells(input.number, 4).setValue(input.indicator);
            this.inputGrid.cells(input.number, 5).setValue(input.colour);
        });
    }

    getConfig(): AssetInputDTO[] {
        const inputs: AssetInputDTO[] = [];

        for (let i = 1; i < 4 + 1; i++) {
            const input = {} as AssetInputDTO;

            input.enabled = this.inputGrid.cells(i, 1).isChecked();
            input.invert = this.inputGrid.cells(i, 2).isChecked();
            input.number = i;
            input.activity = this.inputGrid.cells(i, 3).getValue();
            input.indicator = this.inputGrid.cells(i, 4).getValue();
            input.colour = this.inputGrid.cells(i, 5).getValue();
            inputs.push(input);
        }
        return inputs;
    }

    public select(assetApplication: string | number) {
        const deviceType = this.owner.deviceForm.getItemValue('device_type');
        return;

        switch (assetApplication) {
            case 'MOWER': {
                if (deviceType == 'Cypress CTM-ONE ') {
                    this.inputGrid.cells(1, 1).setValue('1');
                    this.inputGrid.cells(1, 3).setValue('MOWER');
                    this.inputGrid.cells(1, 4).setValue('BACK');
                    this.inputGrid.cells(1, 5).setValue('#00FF00');
                }

                break;
            }

            case 'MOTOR_GRADER': {
                let input = 3;

                if (deviceType == 'FMM230') {
                    input = 2;
                }
                this.inputGrid.cells(input, 1).setValue('1');
                this.inputGrid.cells(input, 3).setValue('MOLDBOARD');
                this.inputGrid.cells(input, 4).setValue('BOTTOM');
                this.inputGrid.cells(input, 5).setValue('#FFA500');
                break;
            }

            case 'SALTER_SANDER': {
                this.inputGrid.cells(1, 1).setValue('1');
                this.inputGrid.cells(1, 3).setValue('PLOW');
                this.inputGrid.cells(1, 4).setValue('FRONT');
                this.inputGrid.cells(1, 5).setValue('#FFA500');

                this.inputGrid.cells(2, 1).setValue('1');
                this.inputGrid.cells(2, 3).setValue('BOX');
                this.inputGrid.cells(2, 4).setValue('TOP');
                this.inputGrid.cells(2, 5).setValue('#CCBB23');

                this.inputGrid.cells(3, 1).setValue('1');
                this.inputGrid.cells(3, 3).setValue('TAILGATE');
                this.inputGrid.cells(3, 4).setValue('BOTTOM');
                this.inputGrid.cells(3, 5).setValue('#44A500');

                this.inputGrid.cells(4, 1).setValue('1');
                this.inputGrid.cells(4, 3).setValue('SPREADER');
                this.inputGrid.cells(4, 4).setValue('BACK');
                this.inputGrid.cells(4, 5).setValue('#544323');
                break;
            }
        }
    }
}

//====================================================================================================================================================================================================
class EditAssetInformation {
    public form: dhtmlXForm;
    private owner: VisionAssetWindow;
    private selectedAsset!: AssetDTO;

    constructor(owner: VisionAssetWindow) {
        this.owner = owner;
        this.form = this.owner.sideBar.cells('asset_info').attachForm({});
        this.buildForm();
    }

    private buildForm(): void {
        const details_form_data = [
            {
                type: 'settings',
                position: 'label-left',
                labelWidth: 200,
                inputWidth: 130,
            },
            {
                name: 'id',
                type: 'input',
                label: 'ID',
                value: '',
                inputWidth: '400',
                offsetLeft: '25',
                offsetTop: '25',
                disabled: true,
            },
            {
                name: 'name',
                type: 'input',
                label: 'Name',
                value: '',
                inputWidth: '400',
                offsetLeft: '25',
                validate: 'ValidAplhaNumeric',
                required: true,
            },
            {
                name: 'description',
                type: 'input',
                label: 'Description',
                value: '',
                rows: '3',
                inputWidth: '400',
                offsetLeft: '25',
            },
        ];
        this.form.loadStruct(details_form_data);
        this.form.attachEvent('onChange', () => {
            this.owner.setDirty();
        });
    }

    update(asset: AssetDTO): void {
        this.selectedAsset = asset;
        this.clearForm();

        this.form.setItemValue('id', asset.id.id);
        this.form.setItemValue('name', asset.id.name);
        this.form.setItemValue('description', asset.description);
    }

    clearForm() {
        this.form.setItemValue('id', '');
        this.form.setItemValue('name', '');
        this.form.setItemValue('description', '');
        this.form.setItemValue('background_colour', '#000000');
        this.form.setItemValue('text_colour', '#FFFFFF');
    }
}

/** Used to edit and configure asset information.
 *
 */
export class VisionAssetWindow extends VisionWindow {
    public dirty = false;

    private layout!: dhtmlXLayoutObject;
    public sideBar!: dhtmlXSideBar;
    private beaconsForm!: dhtmlXForm;
    public deviceForm!: dhtmlXForm;
    public displayForm!: dhtmlXForm;
    public roleForm!: dhtmlXForm;
    public apiForm!: dhtmlXForm;

    public spreaderMaterialsList!: dhtmlXGridObject;

    private assetListGrid!: dhtmlXGridObject;
    public service: AssetService;
    private typeService: AssetService;
    public assetListCache: Map<string | number, AssetDTO>;

    private information: EditAssetInformation;
    public inputData: EditAssetInputData;
    public analogInputData: EditAssetAnalogInputs;

    public assetTypeCache!: Map<string | number, AssetTypeDTO>;
    private toolbar!: dhtmlXToolbarObject;
    private selectedAsset: string | null;

    constructor(manager: VisionWindowManager) {
        super(manager, {
            id: 'edit_asset_window',
            title: 'Assets',
            x: 10,
            y: 10,
            width: 1000,
            height: 600,
        });
        this.window.setText("<div style='display: flex;'><img src='icons/avl-tab.png'/>&nbsp;&nbsp;Assets </div>");
        this.modal = true;
        this.selectedAsset = null;

        this.service = this.manager.app.assetService;
        this.typeService = this.manager.app.assetService;

        this.assetListCache = new Map<string | number, AssetDTO>();

        this.window.attachEvent('onClose', (win) => {
            if (this.dirty) {
                dhtmlx.message({
                    title: 'Unsaved Changes',
                    type: 'error',
                    text: 'You have unsaved changes. Please save or discard your changes before selecting another asset.',
                });
                return false;
            } else {
                return true;
            }
        });

        this.buildLayout();
        this.loadAssets();

        this.information = new EditAssetInformation(this);
        this.inputData = new EditAssetInputData(this);
        this.analogInputData = new EditAssetAnalogInputs(this);
    }

    public setDirty(): void {
        this.dirty = true;
        this.toolbar.showItem('discard');
    }

    private loadAssets(): void {
        this.assetListGrid.clearAll(false);
        const assets = this.service.getAssetList();
        assets.then((data) => {
            if (data.parsedBody) {
                data.parsedBody.forEach((element) => {
                    this.assetListCache.set(element.id.id, element);
                    this.addToAssetListTable(element);
                });
                if (this.selectedAsset != null) {
                    this.assetListGrid.selectRowById(this.selectedAsset, false, true, true);
                    this.sideBar.items('asset_info').setActive();
                }
            }
        });
    }

    private addToAssetListTable(element: AssetDTO): void {
        this.assetListGrid.deleteRow(element.id.id);

        const elementData = new Array<string>();
        elementData.push(element.id.name);
        if (element.type != null) {
            elementData.push(element.type.name);
        } else {
            elementData.push('none');
        }

        this.assetListGrid.addRow(element.id.id, elementData, 0, 0, '', false);
    }

    private buildLayout(): void {
        this.layout = this.window.attachLayout('2U');
        this.layout.cells('a').setText('Assets');
        this.layout.cells('a').setWidth(200);
        this.layout.cells('a').hideHeader();

        this.layout.cells('b').setText('Configuration2');

        this.assetListGrid = this.layout.cells('a').attachGrid();
        this.assetListGrid.setHeader('Name', '', []);
        this.assetListGrid.setColSorting('int');
        this.assetListGrid.init();

        this.assetListGrid.attachEvent('onBeforeSelect', (id, _ind) => {
            if (this.dirty) {
                dhtmlx.message({
                    title: 'Unsaved Changes',
                    type: 'error',
                    text: 'You have unsaved changes. Please save or discard your changes before selecting another asset.',
                });
                return false;
            }
            return true;
        });

        this.assetListGrid.attachEvent('onRowSelect', (id, _ind) => {
            this.onAssetListSelected(id);
        });

        // Build the side bar elements.
        this.sideBar = this.layout.cells('b').attachSidebar();
        this.sideBar.addItem({
            id: 'asset_info',
            text: 'Information',
            icon: 'icons/information.png',
        });
        this.sideBar.addItem({
            id: 'asset_role',
            text: 'Roles',
            icon: 'icons/activity.png',
        });
        this.sideBar.addItem({
            id: 'asset_icon',
            text: 'Display',
            icon: 'icons/link.png',
        });
        this.sideBar.addItem({
            id: 'asset_digital_inputs',
            text: 'Digital Inputs',
            icon: 'icons/meta.png',
        });
        this.sideBar.addItem({
            id: 'asset_analog_inputs',
            text: 'Analog Inputs',
            icon: 'icons/meta.png',
        });
        this.sideBar.addItem({
            id: 'asset_beacons',
            text: 'Beacons',
            icon: 'icons/bluetooth.png',
        });
        this.sideBar.addItem({
            id: 'asset_device',
            text: 'Device',
            icon: 'icons/device.png',
        });
        this.sideBar.addItem({
            id: 'asset_spreader',
            text: 'Spreader Materials',
            icon: 'icons/meta.png',
        });
        this.sideBar.addItem({
            id: 'asset_api',
            text: 'API',
            icon: 'icons/code.png',
        });

        const form_data = [
            {
                type: 'settings',
                position: 'label-left',
                labelWidth: 90,
                inputWidth: 130,
            },
            {
                name: 'beacons',
                type: 'checkbox',
                label: 'Using Plow Beacon',
                offsetLeft: '25',
                offsetTop: '25',
            },
        ];
        this.beaconsForm = this.sideBar.cells('asset_beacons').attachForm(form_data);

        const device_data = [
            {
                type: 'settings',
                position: 'label-left',
                labelWidth: 90,
                inputWidth: 130,
            },
            {
                name: 'device_type',
                type: 'input',
                label: 'Type',
                offsetLeft: '25',
                offsetTop: '25',
                disabled: true,
            },
            {
                name: 'device_imei',
                type: 'input',
                label: 'IMEI',
                offsetLeft: '25',
                offsetTop: '25',
                disabled: true,
            },
            {
                name: 'device_firmware',
                type: 'input',
                label: 'Firmware',
                offsetLeft: '25',
                offsetTop: '25',
                disabled: true,
            },
            {
                name: 'sim',
                type: 'input',
                label: 'SIM',
                offsetLeft: '25',
                offsetTop: '25',
                disabled: true,
                inputWidth: 200,
            },
            {
                name: 'sim_status',
                type: 'input',
                label: 'Status',
                offsetLeft: '25',
                offsetTop: '25',
                disabled: true,
            },
            {
                name: 'sim_usage',
                type: 'input',
                label: 'Usage',
                offsetLeft: '25',
                offsetTop: '25',
                disabled: true,
            },
            {
                name: 'sim_last_seen',
                type: 'input',
                label: 'Seen',
                offsetLeft: '25',
                offsetTop: '25',
                disabled: true,
                inputWidth: 200,
            },
        ];
        this.deviceForm = this.sideBar.cells('asset_device').attachForm(device_data);
        this.spreaderMaterialsList = this.sideBar.cells('asset_spreader').attachGrid();
        this.spreaderMaterialsList.setColumnIds('solid,prewet,antiice');
        this.spreaderMaterialsList.setHeader('Solid,Prewet,Antiice', '', ['text-align:center;', 'text-align:center', 'text-align:center']);
        this.spreaderMaterialsList.setColTypes('ed,ed,ed');
        this.spreaderMaterialsList.setColAlign('center,center,center');
        this.spreaderMaterialsList.init();

        this.spreaderMaterialsList.addRow(1, ',,,', '', '', '', false);
        this.spreaderMaterialsList.addRow(2, ',,,', '', '', '', false);
        this.spreaderMaterialsList.addRow(3, ',,,', '', '', '', false);
        this.spreaderMaterialsList.addRow(4, ',,,', '', '', '', false);

        const display_data = [
            {
                type: 'fieldset',
                name: 'iconslabels',
                label: 'Icon & Labels',
                width: 400,
                offsetLeft: '25',
                list: [
                    {
                        type: 'settings',
                        position: 'label-left',
                        labelWidth: 90,
                        inputWidth: 130,
                    },
                    {
                        name: 'asset_icon',
                        type: 'select',
                        label: 'Asset Icon',
                        value: '',
                        offsetLeft: '25',
                        options: [
                            { text: 'None', value: 'none' },
                            { text: 'Motograder', value: 'grader' },
                            { text: 'Dump Truck', value: 'dumptruck' },
                            { text: 'Pickup', value: 'pickup' },
                            { text: 'Loader', value: 'loader' },
                            { text: 'Snow Plow', value: 'plow' },
                            { text: 'Excavator', value: 'excavator' },
                            { text: 'SUV', value: 'suv' },
                            { text: 'Car', value: 'car' },
                            { text: 'ATV', value: 'atv' },
                            { text: 'Tractor', value: 'tractor' },
                        ],
                    },

                    {
                        type: 'colorpicker',
                        name: 'asset_colour',
                        label: 'Color:',
                        offsetLeft: '25',
                        value: '#000000',
                        enableCustomColors: true,
                    },
                    {
                        type: 'colorpicker',
                        name: 'background_colour',
                        label: 'Label Color:',
                        offsetLeft: '25',
                        value: '#000000',
                        enableCustomColors: true,
                    },

                    {
                        type: 'colorpicker',
                        name: 'text_colour',
                        label: 'Text Color:',
                        offsetLeft: '25',
                        value: '#FFFFFF',
                        enableCustomColors: true,
                    },
                    {
                        name: 'example_label',
                        type: 'input',
                        label: '',
                        offsetLeft: '25',
                        offsetTop: '25',
                        disabled: true,
                        value: 'Asset 123',
                    },
                ],
            },

            {
                type: 'fieldset',
                name: 'display',
                label: 'Advanced Settings',
                width: 400,
                offsetLeft: '25',
                list: [
                    {
                        name: 'camera_rotate',
                        type: 'checkbox',
                        labelWidth: 150,
                        label: 'Rotate Camera',
                        offsetLeft: '0',
                        offsetTop: '0',
                        checked: false,
                    },
                    {
                        type: 'colorpicker',
                        name: 'track_colour',
                        label: 'Track Color:',
                        offsetLeft: '25',
                        value: '#FF00FF',
                        enableCustomColors: true,
                    },
                ],
            },
        ];

        const roles_form_data = [
            {
                type: 'fieldset',
                name: 'roles_data',
                label: 'Roles and Application Info',
                width: 500,
                offsetLeft: '25',
                list: [
                    {
                        type: 'settings',
                        position: 'label-left',
                        labelWidth: 250,
                        inputWidth: 130,
                    },
                    {
                        name: 'application',
                        type: 'select',
                        label: 'Primary',
                        value: '',
                        required: true,
                        options: [
                            { text: 'None', value: 'NONE' },
                            { text: 'Motor Grader', value: 'MOTOR_GRADER' },
                            { text: 'Mower', value: 'MOWER' },
                            { text: 'Salter/Sander', value: 'SALTER_SANDER' },
                            { text: 'Rocker', value: 'ROCKER' },
                            { text: 'Sweeper', value: 'SWEEPER' },
                            { text: 'Pickup', value: 'PICKUP' },
                            { text: 'Light-Duty', value: 'LIGHT_DUTY' },
                        ],
                    },
                    {
                        name: 'secondary',
                        type: 'multiselect',
                        label: 'Secondary',
                        value: '',
                        connector: null,
                        size: 3,
                        tooltip: 'Use Ctrl-Click to select multiple roles for equipment',
                        options: [
                            { text: 'Motor Grader', value: 1 },
                            { text: 'Mower', value: 2 },
                            { text: 'Salter/Sander', value: 4 },
                            { text: 'Rocker', value: 8 },
                            { text: 'Sweeper', value: 16 },
                            { text: 'Pickup', value: 32 },
                            { text: 'Light-Duty', value: 64 },
                        ],
                    },

                    {
                        name: 'spreader',
                        type: 'select',
                        label: 'Spreader Controller',
                        value: '',
                        required: false,
                        hidden: false,
                        options: [
                            { text: 'None', value: 'NONE' },
                            //{text: "CS440", value: "cs440"},
                            //{text: "CS550", value: "cs550"},
                            { text: 'Force 5100ex', value: 'force_5100_ex' },
                            { text: 'Epoke', value: 'epoke' },
                            { text: 'Cirus Spreadsmart', value: 'cirus_spreadsmart' },
                        ],
                    },
                    {
                        name: 'using_spreader_pulse',
                        type: 'checkbox',
                        label: 'Using Spreader Pulses',
                        offsetLeft: '0',
                        offsetTop: '0',
                        checked: false,
                        hidden: true,
                    },
                    {
                        name: 'grade_with_speed',
                        type: 'checkbox',
                        //label: 'Use Speed for Grading Events <span style="cursor:help; border:1px solid #999; border-radius:50%; padding:0px 5px; font-weight:bold; background:#eee;">?</span>',
                        label: "Used Speed for Grading<i class='fa-solid fa-circle-question' title='If checked, grading events will be determined by motorgrader speed' style='cursor:help; margin-left:5px; color:#39C;'></i>",
                        offsetLeft: '0',
                        offsetTop: '0',
                        checked: false,
                        hidden: true,
                        tooltip: 'If checked, grading events will be determined by motorgrader speed.',
                    },
                    {
                        name: 'grade_with_speed_km',
                        type: 'input',
                        //label: 'Use Speed for Grading Events <span style="cursor:help; border:1px solid #999; border-radius:50%; padding:0px 5px; font-weight:bold; background:#eee;">?</span>',
                        label: 'Grading Speed(max)',
                        offsetLeft: '0',
                        offsetTop: '0',
                        checked: false,
                        hidden: true,
                        tooltip: 'The max speed for grader to be grading',
                    },
                    {
                        name: 'grade_with_speed_time',
                        type: 'input',
                        //label: 'Use Speed for Grading Events <span style="cursor:help; border:1px solid #999; border-radius:50%; padding:0px 5px; font-weight:bold; background:#eee;">?</span>',
                        label: 'Time (seconds)</i>',
                        offsetLeft: '0',
                        offsetTop: '0',
                        checked: false,
                        hidden: true,
                        tooltip: 'Time at grading speed to be an event.',
                    },
                    {
                        name: 'mowing_with_speed',
                        type: 'checkbox',
                        //label: 'Use Speed for Grading Events <span style="cursor:help; border:1px solid #999; border-radius:50%; padding:0px 5px; font-weight:bold; background:#eee;">?</span>',
                        label: "Used Speed for Mowing<i class='fa-solid fa-circle-question' title='If checked, grading events will be determined by mower's speed' style='cursor:help; margin-left:5px; color:#39C;'></i>",
                        offsetLeft: '0',
                        offsetTop: '0',
                        checked: false,
                        hidden: true,
                        tooltip: 'If checked, mowing events will be determined by mower speed.',
                    },
                    {
                        name: 'mowing_with_speed_km',
                        type: 'input',
                        label: 'Mowing Speed(max)',
                        offsetLeft: '0',
                        offsetTop: '0',
                        checked: false,
                        hidden: true,
                        tooltip: 'The max speed for grader to be grading',
                    },
                    {
                        name: 'mowing_with_speed_time',
                        type: 'input',
                        label: 'Time (seconds)</i>',
                        offsetLeft: '0',
                        offsetTop: '0',
                        checked: false,
                        hidden: true,
                        tooltip: 'Time at mowing speed to be an event.',
                    },
                    {
                        name: 'spreader_amount_per_pulse',
                        type: 'input',
                        label: 'Amount per Pulse',
                        hidden: true,
                    },
                    {
                        name: 'rock_capacity',
                        type: 'input',
                        label: 'Rock Capacity(tons)',
                        value: '',
                        required: false,
                        hidden: false,
                    },
                ],
            },
        ];

        this.roleForm = this.sideBar.cells('asset_role').attachForm(roles_form_data);
        this.roleForm.attachEvent('onChange', (name: string, value: string | number, _state: boolean) => {
            this.dirty = true;
            this.toolbar.showItem('discard');
            if (name == 'using_spreader_pulse') {
                if (_state == false) {
                    this.roleForm.hideItem('spreader_amount_per_pulse');
                } else {
                    this.roleForm.showItem('spreader_amount_per_pulse');
                }
            }

            console.log('Secondary :', this.roleForm.getItemValue('secondary'));

            const isGrader = this.roleForm.getItemValue('application') == 'MOTOR_GRADER' || this.roleForm.getItemValue('secondary').includes('1');
            const isMower = this.roleForm.getItemValue('application') == 'MOWER' || this.roleForm.getItemValue('secondary').includes('2');

            this.roleForm.hideItem('grade_with_speed');
            this.roleForm.hideItem('grade_with_speed_km');
            this.roleForm.hideItem('grade_with_speed_time');
            this.roleForm.hideItem('mowing_with_speed');
            this.roleForm.hideItem('mowing_with_speed_km');
            this.roleForm.hideItem('mowing_with_speed_time');

            if (isGrader) {
                this.roleForm.showItem('grade_with_speed');
                this.roleForm.showItem('grade_with_speed_km');
                this.roleForm.showItem('grade_with_speed_time');
            }
            if (isMower) {
                this.roleForm.showItem('mowing_with_speed');
                this.roleForm.showItem('mowing_with_speed_km');
                this.roleForm.showItem('mowing_with_speed_time');
            }
        });

        this.sideBar.cells('asset_icon').attachHTMLString("<DIV id='form_data'></DIV><DIV><img id='asset_icon_show' src='' height='40' width='40'/></DIV>");
        this.displayForm = new dhtmlXForm('form_data');
        this.displayForm.loadStruct(display_data);

        this.displayForm.attachEvent('onChange', (name: string, value: string | number, _state: boolean) => {
            this.iconFormChangeEvent(name, value);
            this.setDirty();
        });

        //--- Update the colour picker with the values that were in localstorage ( if any )
        const assetColourPicker = this.displayForm.getColorPicker('asset_colour');
        const labelColourPicker = this.displayForm.getColorPicker('background_colour');
        const textColourPicker = this.displayForm.getColorPicker('text_colour');

        const savedAssetColours = localStorage.getItem('saved_asset_colours');
        const savedLabelColours = localStorage.getItem('saved_label_colours');
        const savedTextColours = localStorage.getItem('saved_text_colours');

        assetColourPicker.setCustomColors(savedAssetColours);
        labelColourPicker.setCustomColors(savedLabelColours);
        textColourPicker.setCustomColors(savedTextColours);

        // -- Update the local storage when the user clicks on a colour
        assetColourPicker.attachEvent('onSaveColor', (_colour: string) => {
            localStorage.setItem('saved_asset_colours', assetColourPicker.getCustomColors());
        });

        labelColourPicker.attachEvent('onSaveColor', (_colour: string) => {
            localStorage.setItem('saved_label_colours', labelColourPicker.getCustomColors());
        });

        textColourPicker.attachEvent('onSaveColor', (_colour: string) => {
            localStorage.setItem('saved_text_colours', textColourPicker.getCustomColors());
        });

        const api_data = [
            {
                type: 'fieldset',
                name: 'display',
                label: 'Advanced Settings',
                width: 400,
                offsetLeft: '25',
                list: [
                    {
                        name: 'castle_rock',
                        type: 'checkbox',
                        labelWidth: 150,
                        label: 'Available for 511',
                        offsetLeft: '0',
                        offsetTop: '0',
                        checked: false,
                    },
                    { name: 'rabbit', type: 'input', label: 'Rabbit MQ Topic' },
                ],
            },
        ];
        this.apiForm = this.sideBar.cells('asset_api').attachForm(api_data);

        //(document.getElementById('asset_icon_show') as HTMLImageElement).src = 'data:image/svg+xml,' + encodeURIComponent(SVGIcons.grader(false, "blue"));

        this.toolbar = this.layout.cells('b').attachToolbar();
        this.toolbar.addButton('new', 0, 'New', 'icons/sign.png', '');
        this.toolbar.addButton('save', 1, 'Save', 'css/imgs/save.gif', '');
        this.toolbar.addSeparator('sep1', 2);
        this.toolbar.addButton('delete', 3, 'Delete', 'icons/clear.png', '');
        this.toolbar.addButton('discard', 4, 'Discard Changes', 'icons/undo.png', '');

        this.toolbar.hideItem('discard');

        this.toolbar.attachEvent('onClick', (itemId) => {
            this.toolbarEvent(itemId);
        });

        this.layout.cells('b').hideHeader();
        this.sideBar.cells('asset_info').setActive();
    }

    /** Fired when the data in the icon edit form has changed.
     *
     * @param name
     */
    private iconFormChangeEvent(name: string, value: string | number) {
        console.log(name);
        console.log(value);
        switch (name) {
            case 'asset_icon': {
                const colour = this.displayForm.getItemValue('asset_colour');
                this.updateIconDisplay(value, colour);
                break;
            }
            case 'asset_colour': {
                const asset = this.displayForm.getItemValue('asset_icon');
                this.updateIconDisplay(asset, value as string);
                break;
            }
            case 'background_colour':
                this.displayForm.getInput('example_label').style.backgroundColor = value;
                break;
            case 'text_colour':
                this.displayForm.getInput('example_label').style.color = value;
                break;
        }
    }

    private updateIconDisplay(name: string | number, colour: string) {
        if (name == undefined) {
            (document.getElementById('asset_icon_show') as HTMLImageElement).style.display = 'none';
        } else {
            (document.getElementById('asset_icon_show') as HTMLImageElement).style.display = 'block';
        }

        if (name == 'none') {
            (document.getElementById('asset_icon_show') as HTMLImageElement).style.display = 'none';
        } else {
            (document.getElementById('asset_icon_show') as HTMLImageElement).src = 'data:image/svg+xml,' + encodeURIComponent(SVGIcons.getIcon(name as string, false, colour));
        }
    }

    public selectAsset(id: string) {
        this.selectedAsset = id;
    }

    /** Fired when an asset is selected from the grid.
     *  Populates the form with the information for that asset.
     *
     * @param id The id of the Asset that was selected.
     */
    public onAssetListSelected(id: string | number): void {
        const asset = this.assetListCache.get(id);

        //this.assignedDevices.greyExisting();
        if (asset) {
            //this.assignedDevices.highlight(asset);
            this.information.update(asset);
            this.inputData.update(asset);
            this.analogInputData.update(asset);

            if (asset.config.usingBeacons == true) {
                this.beaconsForm.checkItem('beacons');
            } else {
                this.beaconsForm.uncheckItem('beacons');
            }

            if (asset.device == null) {
                this.service.getDeviceInfo(asset.id.id).then((response) => {
                    if (response.parsedBody) {
                        asset.device = response.parsedBody;
                        this.assetListCache.set(id, asset);
                        this.setDeviceInfo(asset);
                    }
                });
            } else {
                this.setDeviceInfo(asset);
            }

            this.updateDisplayForm(asset);
            if (asset.available511 == true) {
                this.apiForm.checkItem('castle_rock');
            } else {
                this.apiForm.uncheckItem('castle_rock');
            }

            this.updateRolesForm(asset);
            this.setSpreaderMaterials(asset);
        }
    }

    /** Sets up the grid that has the mateiral names for the spreader.
     *
     * @param asset The asset we are editing.
     */
    private setSpreaderMaterials(asset: AssetDTO): void {
        this.spreaderMaterialsList.clearAll(false);

        if (asset.config.solidMaterials != null) {
            let rowid = 0;
            asset.config.solidMaterials.forEach((material) => {
                if (this.spreaderMaterialsList.getRowsNum() < rowid + 1) {
                    this.spreaderMaterialsList.addRow(rowid + 1, ',,,', '', '', '', false);
                }
                this.spreaderMaterialsList.cells(rowid + 1, 0).setValue(material);
                rowid++;
            });
        }
        if (asset.config.prewetMaterials != null) {
            let rowid = 0;
            asset.config.prewetMaterials.forEach((material) => {
                if (this.spreaderMaterialsList.getRowsNum() < rowid + 1) {
                    this.spreaderMaterialsList.addRow(rowid + 1, ',,,', '', '', '', false);
                }
                this.spreaderMaterialsList.cells(rowid + 1, 1).setValue(material);
                rowid++;
            });
        }
        if (asset.config.antiiceMaterials != null) {
            let rowid = 0;
            asset.config.antiiceMaterials.forEach((material) => {
                if (this.spreaderMaterialsList.getRowsNum() < rowid + 1) {
                    this.spreaderMaterialsList.addRow(rowid + 1, ',,,', '', '', '', false);
                }
                this.spreaderMaterialsList.cells(rowid + 1, 2).setValue(material);
                rowid++;
            });
        }
    }

    private setDeviceInfo(asset: AssetDTO): void {
        this.deviceForm.setItemValue('device_type', asset.device.type);
        this.deviceForm.setItemValue('device_imei', asset.device.networkId1);
        this.deviceForm.setItemValue('sim', asset.device.sim);
        this.deviceForm.setItemValue('device_firmware', asset.device.firmware);
        this.deviceForm.setItemValue('sim_status', asset.device.simStatus);
        this.deviceForm.setItemValue('sim_usage', asset.device.usage);
        this.deviceForm.setItemValue('sim_last_seen', asset.device.lastSeen);
    }

    private updateRolesForm(asset: AssetDTO) {
        this.roleForm.setItemValue('application', asset.assetType);

        if (asset.type != null) {
            this.roleForm.setItemValue('application', asset.type.name);
        }

        this.roleForm.setItemValue('rock_capacity', Measurement.kg2ImperialTons(asset.config.rockCapacity));
        this.roleForm.setItemValue('spreader', asset.config.spreader);
        this.roleForm.setItemValue('secondary', this.getBitwiseComponents(asset.secondary));

        if (asset.config.usingSpreaderPulse == true) {
            this.roleForm.checkItem('using_spreader_pulse');
            this.roleForm.showItem('spreader_amount_per_pulse');
            this.roleForm.setItemValue('spreader_amount_per_pulse', Measurement.kg2pounds(asset.config.spreaderPulseKg));
        } else {
            this.roleForm.uncheckItem('using_spreader_pulse');
            this.roleForm.hideItem('spreader_amount_per_pulse');
            this.roleForm.setItemValue('spreader_amount_per_pulse', '');
        }

        if (asset.assetType == 'MOTOR_GRADER' || (asset.secondary != null && (asset.secondary & 1) == 1)) {
            this.roleForm.showItem('grade_with_speed');
            this.roleForm.showItem('grade_with_speed_km');
            this.roleForm.showItem('grade_with_speed_time');

            this.roleForm.setItemValue('grade_with_speed_km', Measurement.km2miles(asset.config.gradingWithSpeedKm).toFixed(1));
            this.roleForm.setItemValue('grade_with_speed_time', asset.config.gradingWithSpeedTimeSec);
            if (asset.config.gradingWithSpeed == true) {
                this.roleForm.checkItem('grade_with_speed');
            } else {
                this.roleForm.uncheckItem('grade_with_speed');
            }
        }

        if (asset.assetType == 'MOWER' || (asset.secondary != null && (asset.secondary & 2) == 2)) {
            this.roleForm.showItem('mowing_with_speed');
            this.roleForm.showItem('mowing_with_speed_km');
            this.roleForm.showItem('mowing_with_speed_time');

            this.roleForm.setItemValue('mowing_with_speed_km', Measurement.km2miles(asset.config.mowingWithSpeedKm).toFixed(1));
            this.roleForm.setItemValue('mowing_with_speed_time', asset.config.mowingWithSpeedTimeSec);
            if (asset.config.mowingWithSpeed == true) {
                this.roleForm.checkItem('mowing_with_speed');
            } else {
                this.roleForm.uncheckItem('mowing_with_speed');
            }
        }
    }

    private updateDisplayForm(asset: AssetDTO) {
        const icon = asset.config.icon;
        const colour = asset.config.colour;
        this.updateIconDisplay(icon, colour);

        this.displayForm.uncheckItem('camera_rotate');

        if (asset.config.trackColour != null) {
            this.displayForm.setItemValue('track_colour', asset.config.trackColour);
        }
        if (asset.config.cameraRotate == true) {
            this.displayForm.checkItem('camera_rotate');
        }

        if (icon != undefined) {
            this.displayForm.setItemValue('asset_icon', icon);
            this.displayForm.setItemValue('asset_colour', colour);
        } else {
            this.displayForm.setItemValue('asset_icon', 'none');
            this.displayForm.setItemValue('asset_colour', '#000000');
        }

        if (asset.config.labelColour != null) {
            this.displayForm.getInput('example_label').style.backgroundColor = asset.config.labelColour;
            this.displayForm.setItemValue('background_colour', asset.config.labelColour);
        } else {
            this.displayForm.getInput('example_label').style.backgroundColor = '#000000';
            this.displayForm.setItemValue('background_colour', '#000000');
        }

        if (asset.config.textColour != null) {
            this.displayForm.setItemValue('text_colour', asset.config.textColour);
            this.displayForm.getInput('example_label').style.color = asset.config.textColour;
        } else {
            this.displayForm.getInput('example_label').style.color = '#FFFFFF';
            this.displayForm.setItemValue('text_colour', '#FFFFFF');
        }
    }

    getBitwiseComponents(n: number): number[] {
        const result: number[] = [];
        let bit = 1;

        while (n > 0) {
            if (n & bit) {
                result.push(bit);
                n -= bit;
            }
            bit <<= 1;
        }

        return result;
    }

    private clearForm() {
        this.sideBar.items('asset_info').setActive();
        this.information.clearForm();
        this.assetListGrid.clearSelection();
        this.beaconsForm.uncheckItem('beacons');
        this.deviceForm.setItemValue('device_type', '');
        this.deviceForm.setItemValue('device_imei', '');
        this.deviceForm.setItemValue('sim', '');
        this.deviceForm.setItemValue('device_firmware', '');
        this.deviceForm.setItemValue('sim_status', '');
        this.deviceForm.setItemValue('sim_usage', '');
        this.deviceForm.setItemValue('sim_last_seen', '');

        this.displayForm.uncheckItem('camera_rotate');
        this.apiForm.uncheckItem('castle_rock');
        this.roleForm.setItemValue('rock_capacity', '0.0');
        this.roleForm.setItemValue('spreader', 'NONE');
        this.roleForm.setItemValue('application', 'NONE');
        this.roleForm.setItemValue('secondary', []);

        this.roleForm.uncheckItem('grade_with_speed');
        this.roleForm.setItemValue('grade_with_speed_km', '');
        this.roleForm.setItemValue('grade_with_speed_time', '');

        this.spreaderMaterialsList.clearAll(false);
    }

    /** Handles when the toolbar is clicked.
     *
     * @param action One of new | save
     */
    private toolbarEvent(action: string) {
        switch (action) {
            case 'new': {
                this.clearForm();
                break;
            }

            case 'save': {
                this.saveAsset();
                break;
            }

            case 'delete': {
                this.deleteAsset();
                break;
            }

            case 'discard': {
                this.dirty = false;
                const id = this.assetListGrid.getSelectedRowId();
                const activeSidebar = this.sideBar.getActiveItem();

                this.toolbar.hideItem('discard');

                this.clearForm();
                this.assetListGrid.selectRowById(id, true, false, false);
                this.sideBar.items(activeSidebar).setActive();
                this.onAssetListSelected(id);
                break;
            }
        }
    }

    private deleteAsset() {
        const id = this.information.form.getItemValue('id');
        if (id.length <= 0) {
            dhtmlx.message({
                title: 'No Asset',
                text: 'No Asset was selected',
                type: 'alert-warning',
            });
            return;
        }

        dhtmlx.confirm({
            type: 'confirm-warning',
            title: 'Delete Asset',
            ok: 'Delete Asset',
            cancel: 'Cancel',
            text: 'WARNING:  This will not only delete the asset but any telemetry data/events/reports with that asset. This data will not be recoverable and will be lost',
            callback: (result: boolean) => {
                if (result == false) {
                    return;
                } else {
                    this.sendDeleteToServer();
                }
            },
        });
    }

    private sendDeleteToServer(): void {
        const id = this.information.form.getItemValue('id');

        VisionApplication.getInstance()
            .assetService.deleteAsset(id)
            .then((_data) => {
                dhtmlx.message({
                    title: 'Deleted',
                    text: 'Asset was removed',
                    type: 'alert-success',
                });

                this.clearForm();
                this.assetListCache.delete(id);
                this.assetListGrid.deleteRow(id);
                return;
            });
    }

    /** Save an asset.
     * Makes an API call to server to save the asest information.
     * Will pop up a message when complete.
     *
     * If no ID is available it will send as a POST to create a new asset. Otherwise it will do a PUT to update an existing one based on the ID.
     */
    private saveAsset(): void {
        this.dirty = false;

        const save = {} as AssetDTO;

        save.id = {} as IdName;
        save.id.id = this.information.form.getItemValue('id');
        save.id.name = this.information.form.getItemValue('name');
        save.description = this.information.form.getItemValue('description');
        //save.assignedDevices = this.assignedDevices.getDevices();
        save.config = {} as AssetConfigurationDTO;
        save.config.inputs = this.inputData.getConfig();
        save.config.analog = this.analogInputData.getConfig();
        save.assetType = this.roleForm.getItemValue('application');
        const secondary = this.roleForm.getItemValue('secondary');
        let secondaryflag = 0;
        for (const flag of secondary) {
            secondaryflag += Number(flag);
        }

        save.secondary = secondaryflag;

        save.config.labelColour = this.displayForm.getItemValue('background_colour');
        save.config.textColour = this.displayForm.getItemValue('text_colour');
        save.config.usingBeacons = this.beaconsForm.isItemChecked('beacons');
        save.config.icon = this.displayForm.getItemValue('asset_icon');
        save.config.colour = this.displayForm.getItemValue('asset_colour');

        save.config.trackColour = this.displayForm.getItemValue('track_colour');
        save.config.cameraRotate = this.displayForm.isItemChecked('camera_rotate');

        save.config.solidMaterials = new Array<string>();
        save.config.prewetMaterials = new Array<string>();
        save.config.antiiceMaterials = new Array<string>();
        save.config.rockCapacity = Measurement.imperialTons2Kg(this.roleForm.getItemValue('rock_capacity'));
        save.config.spreader = this.roleForm.getItemValue('spreader');
        save.config.usingSpreaderPulse = this.roleForm.isItemChecked('using_spreader_pulse');
        save.config.spreaderPulseKg = Measurement.pounds2kg(this.roleForm.getItemValue('spreader_amount_per_pulse'));
        save.available511 = this.apiForm.isItemChecked('castle_rock');

        save.config.gradingWithSpeed = this.roleForm.isItemChecked('grade_with_speed');
        save.config.gradingWithSpeedKm = Measurement.miles2km(this.roleForm.getItemValue('grade_with_speed_km'));
        save.config.gradingWithSpeedTimeSec = this.roleForm.getItemValue('grade_with_speed_time');

        save.config.mowingWithSpeed = this.roleForm.isItemChecked('mowing_with_speed');
        save.config.mowingWithSpeedKm = Measurement.miles2km(this.roleForm.getItemValue('mowing_with_speed_km'));
        save.config.mowingWithSpeedTimeSec = this.roleForm.getItemValue('mowing_with_speed_time');

        this.spreaderMaterialsList.forEachRow((id) => {
            const rowData = this.spreaderMaterialsList.getRowData(id);
            if (rowData.solid != '') {
                save.config.solidMaterials.unshift(rowData.solid);
            }
            if (rowData.prewet != '') {
                save.config.prewetMaterials.unshift(rowData.prewet);
            }
            if (rowData.antiice != '') {
                save.config.antiiceMaterials.unshift(rowData.antiice);
            }
        });

        let response;
        if (save.id.id == '') {
            response = this.service.create(save);
        } else {
            response = this.service.updateAsset(save);
        }

        response.then((data) => {
            if (data.parsedBody) {
                const asset = data.parsedBody;
                this.assetListCache.set(asset.id.id, asset);
                this.addToAssetListTable(asset);

                const id = this.assetListGrid.getSelectedRowId();
                const activeSidebar = this.sideBar.getActiveItem();

                this.toolbar.hideItem('discard');

                this.clearForm();
                this.assetListGrid.selectRowById(id, true, false, false);
                this.sideBar.items(activeSidebar).setActive();
                this.onAssetListSelected(id);

                dhtmlx.message({
                    title: 'Save Complete',
                    text: 'Asset ' + data.parsedBody?.id.name + ' was saved.',
                    type: 'alert-success',
                });

                VisionApplication.getInstance().pubsub.publish(PublishTopic.ASSET_INFO_UPDATED, asset);
            }
        });
    }
}
